import type { AeroOptimizationJobDetailResult } from '@/features/tradeLogicAuthoring/api';

type AeroOptimizationJobDetail = NonNullable<AeroOptimizationJobDetailResult['job']>;
type AeroOptionBuyingStrategySummary = NonNullable<AeroOptimizationJobDetail['optionBuyingStrategies']>[number];

export const AERO_OBS_STOCK_KEY = 'stock';

export interface AeroObsPaletteTone {
    key: string;
    label: string;
    shortLabel: string;
    chipClassName: string;
    solidChipClassName: string;
    borderClassName: string;
    ringClassName: string;
    dotClassName: string;
    scatterFill: string;
    barFill: string;
}

const STOCK_TONE: Omit<AeroObsPaletteTone, 'key' | 'label' | 'shortLabel'> = {
    chipClassName: 'bg-zinc-800/80 text-zinc-200 border border-zinc-700',
    solidChipClassName: 'bg-zinc-700 text-zinc-100',
    borderClassName: 'border-zinc-700',
    ringClassName: 'ring-zinc-600',
    dotClassName: 'bg-zinc-400',
    scatterFill: '#a1a1aa',
    barFill: '#a1a1aa'
};

const OBS_TONES: Omit<AeroObsPaletteTone, 'key' | 'label' | 'shortLabel'>[] = [
    {
        chipClassName: 'bg-cyan-900/50 text-cyan-100 border border-cyan-700/70',
        solidChipClassName: 'bg-cyan-600 text-white',
        borderClassName: 'border-cyan-700/70',
        ringClassName: 'ring-cyan-600',
        dotClassName: 'bg-cyan-400',
        scatterFill: '#22d3ee',
        barFill: '#0891b2'
    },
    {
        chipClassName: 'bg-amber-900/50 text-amber-100 border border-amber-700/70',
        solidChipClassName: 'bg-amber-500 text-black',
        borderClassName: 'border-amber-700/70',
        ringClassName: 'ring-amber-500',
        dotClassName: 'bg-amber-400',
        scatterFill: '#fbbf24',
        barFill: '#d97706'
    },
    {
        chipClassName: 'bg-emerald-900/50 text-emerald-100 border border-emerald-700/70',
        solidChipClassName: 'bg-emerald-600 text-white',
        borderClassName: 'border-emerald-700/70',
        ringClassName: 'ring-emerald-600',
        dotClassName: 'bg-emerald-400',
        scatterFill: '#34d399',
        barFill: '#059669'
    },
    {
        chipClassName: 'bg-violet-900/50 text-violet-100 border border-violet-700/70',
        solidChipClassName: 'bg-violet-600 text-white',
        borderClassName: 'border-violet-700/70',
        ringClassName: 'ring-violet-600',
        dotClassName: 'bg-violet-400',
        scatterFill: '#a78bfa',
        barFill: '#7c3aed'
    },
    {
        chipClassName: 'bg-rose-900/50 text-rose-100 border border-rose-700/70',
        solidChipClassName: 'bg-rose-600 text-white',
        borderClassName: 'border-rose-700/70',
        ringClassName: 'ring-rose-600',
        dotClassName: 'bg-rose-400',
        scatterFill: '#fb7185',
        barFill: '#e11d48'
    },
    {
        chipClassName: 'bg-sky-900/50 text-sky-100 border border-sky-700/70',
        solidChipClassName: 'bg-sky-600 text-white',
        borderClassName: 'border-sky-700/70',
        ringClassName: 'ring-sky-600',
        dotClassName: 'bg-sky-400',
        scatterFill: '#38bdf8',
        barFill: '#0284c7'
    },
    {
        chipClassName: 'bg-fuchsia-900/50 text-fuchsia-100 border border-fuchsia-700/70',
        solidChipClassName: 'bg-fuchsia-600 text-white',
        borderClassName: 'border-fuchsia-700/70',
        ringClassName: 'ring-fuchsia-600',
        dotClassName: 'bg-fuchsia-400',
        scatterFill: '#e879f9',
        barFill: '#c026d3'
    },
    {
        chipClassName: 'bg-lime-900/50 text-lime-100 border border-lime-700/70',
        solidChipClassName: 'bg-lime-600 text-black',
        borderClassName: 'border-lime-700/70',
        ringClassName: 'ring-lime-600',
        dotClassName: 'bg-lime-400',
        scatterFill: '#a3e635',
        barFill: '#65a30d'
    }
];

export const buildAeroObsKey = (optionBuyingStrategyId: number | null | undefined): string =>
    optionBuyingStrategyId === null || optionBuyingStrategyId === undefined ? AERO_OBS_STOCK_KEY : `obs-${optionBuyingStrategyId}`;

export const formatAeroObsLabel = (strategy: AeroOptionBuyingStrategySummary): string => {
    const trimmedName = strategy.name?.trim();
    if (trimmedName) return trimmedName;
    if (strategy.openTradeType) return `OBS #${strategy.id} (${strategy.openTradeType})`;

    return `OBS #${strategy.id}`;
};

export const formatAeroObsShortLabel = (strategy: AeroOptionBuyingStrategySummary): string => {
    const trimmedName = strategy.name?.trim();
    if (trimmedName) return trimmedName;

    return `OBS #${strategy.id}`;
};

export interface AeroObsPalette {
    /** Ordered tones for every OBS the job tested (plus stock when present). */
    readonly tones: AeroObsPaletteTone[];
    /** Lookup by OBS key (see buildAeroObsKey). */
    readonly toneByKey: Map<string, AeroObsPaletteTone>;
    /** True if the job actually exercised more than one axis (multiple OBS, or stock + OBS). */
    readonly hasMultipleAxes: boolean;
    /** True if the job exercised at least one OBS. */
    readonly hasAnyObs: boolean;
}

export const buildAeroObsPalette = ({
    optionBuyingStrategies,
    optionBuyingStrategyIdsInResults
}: {
    optionBuyingStrategies: AeroOptionBuyingStrategySummary[] | null | undefined;
    optionBuyingStrategyIdsInResults: Iterable<number | null>;
}): AeroObsPalette => {
    const idsInResults = new Set<number | null>();
    for (const id of optionBuyingStrategyIdsInResults) idsInResults.add(id);

    const strategies = optionBuyingStrategies ?? [];
    const hasStock = idsInResults.has(null) || (strategies.length === 0 && idsInResults.size === 0);

    const tones: AeroObsPaletteTone[] = [];
    const toneByKey = new Map<string, AeroObsPaletteTone>();

    if (hasStock) {
        const stockTone: AeroObsPaletteTone = {
            ...STOCK_TONE,
            key: AERO_OBS_STOCK_KEY,
            label: 'Stock',
            shortLabel: 'Stock'
        };

        tones.push(stockTone);
        toneByKey.set(stockTone.key, stockTone);
    }

    strategies.forEach((strategy, index) => {
        const baseTone = OBS_TONES[index % OBS_TONES.length];

        const tone: AeroObsPaletteTone = {
            ...baseTone,
            key: buildAeroObsKey(strategy.id),
            label: formatAeroObsLabel(strategy),
            shortLabel: formatAeroObsShortLabel(strategy)
        };

        tones.push(tone);
        toneByKey.set(tone.key, tone);
    });

    return {
        tones,
        toneByKey,
        hasMultipleAxes: tones.length > 1,
        hasAnyObs: strategies.length > 0
    };
};

const FALLBACK_TONE: AeroObsPaletteTone = {
    ...STOCK_TONE,
    key: 'unknown',
    label: 'Unknown',
    shortLabel: 'Unknown'
};

export const getAeroObsTone = (palette: AeroObsPalette, optionBuyingStrategyId: number | null | undefined): AeroObsPaletteTone =>
    palette.toneByKey.get(buildAeroObsKey(optionBuyingStrategyId)) ?? FALLBACK_TONE;
